import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useAuthenticatedFetch } from './useAuth';
import {
  applyLanguagePreference,
  readExplicitUserLocale,
  resolveLanguagePreference,
} from '../i18n/languagePreference';

export type Me = {
  id: string;
  name: string;
  email: string;
  role?: string;
  avatarUrl?: string;
  org?: { id: string; name?: string };
  languagePreference?: string;
};

export function useMeQuery() {
  const authenticatedFetch = useAuthenticatedFetch();
  const query = useQuery<Me>({
    queryKey: ['me'] as const,
    queryFn: async () => {
      const resp = await authenticatedFetch('/api/me');
      if (!resp.ok) throw new Error('Failed to fetch current user');
      const j = await resp.json();
      return j.me as Me;
    },
    staleTime: 60_000,
  });

  // - Empty `languagePreference` → keep navigator / i18n detector (GH #1603).
  // - DB often stores default `"en"` while browser is RTL/locale XX → skip clobber unless user
  //   chose English (`localStorage`, set from Team Portal `changeLanguage`): see below.
  useEffect(() => {
    const preference = query.data?.languagePreference;
    if (preference == null || String(preference).trim() === '') return;

    const serverResolved = resolveLanguagePreference(preference);
    const browserResolved =
      typeof navigator !== 'undefined' ? resolveLanguagePreference(navigator.language) : 'en';
    const explicitLanguage = readExplicitUserLocale() === undefined ? serverResolved : 'en'; /* will use app language when no explicit choice */

    // Many accounts persist default `languagePreference: "en"` without a Team Portal choice.
    // That must not override navigator (e.g. Arabic) unless the user explicitly chose English (#1603).
    if (
      serverResolved === 'en' &&
      browserResolved !== 'en' &&
      explicitLanguage !== 'en'
    ) {
      return;
    }

    void applyLanguagePreference(preference);
  }, [query.data?.languagePreference]);

  return query;
}

